import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, ClipboardCheck, Timer, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";

const STATUS = {
  passed: ["Passed", "pill-success"], attention: ["Needs attention", "pill-danger"], open: ["Window open", "pill-warn"],
  missed: ["Missed", "pill-danger"], cancelled: ["Cancelled", ""],
};

function CheckList({ checks }) {
  return (
    <ul className="mt-3 space-y-1.5" data-testid="drill-checks">
      {checks.map((check) => (
        <li key={check.key} className="flex gap-2 text-sm" data-testid={`drill-check-${check.key}`}>
          {check.ok ? <CheckCircle2 size={15} className="shrink-0 mt-0.5" style={{ color: "var(--accent)" }} /> : <XCircle size={15} className="shrink-0 mt-0.5" style={{ color: "#9B2C1F" }} />}
          <span><span style={{ color: "var(--ink)" }}>{check.label}</span> <span className="text-xs" style={{ color: "var(--muted)" }}>· {check.detail}</span></span>
        </li>
      ))}
    </ul>
  );
}

function HandsOn({ identities, openWindow, limits, onStepUp, onChanged, readOnly }) {
  const active = identities.filter((identity) => identity.active);
  const [choice, setChoice] = useState("");
  const selected = choice || active[0]?.id || "";
  const open = () => onStepUp({
    purpose: "breakglass", title: "Confirm practice window",
    onConfirm: async (grant) => {
      await api.post("/admin/breakglass/drills/hands-on", { identity_id: selected, grant });
      toast.success(`Practice window open for ${limits.window_minutes} minutes`);
      onChanged();
    },
  });
  const cancel = async () => {
    try { await api.post(`/admin/breakglass/drills/${openWindow.id}/cancel`); toast.success("Practice window cancelled"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (openWindow) {
    return (
      <div className="p-4 text-sm" style={{ background: "#FBF3E4", color: "#8A5A00" }} data-testid="drill-open-window">
        <Timer size={14} className="inline mr-1.5" />
        {openWindow.identity_name} can sign in from the emergency page until {formatWhen(openWindow.window_ends_at)}
        {" "}({openWindow.failed_attempts} failed attempt(s) so far). The session is view-only and lasts {limits.session_minutes} minutes.
        {!readOnly && <button className="btn-ghost !py-1 !px-2.5 text-xs ml-3" onClick={cancel} data-testid="drill-cancel-window">Cancel window</button>}
      </div>
    );
  }
  if (readOnly) return null;
  if (!active.length) {
    return <p className="text-xs" style={{ color: "var(--muted)" }} data-testid="drill-needs-identity">Create an active emergency identity first.</p>;
  }
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="drill-hands-on">
      <select className="input-flat !w-auto" value={selected} onChange={(e) => setChoice(e.target.value)} disabled={!active.length} data-testid="drill-identity-select">
        {active.map((identity) => <option key={identity.id} value={identity.id}>{identity.display_name}</option>)}
      </select>
      <button className="btn-ghost text-sm" disabled={!active.length} onClick={open} data-testid="drill-open-window-btn">Open {limits.window_minutes}-minute practice window</button>
    </div>
  );
}

export function DrillCard({ breakglass, onStepUp }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const readOnly = breakglass.viewer_is_emergency;

  const load = useCallback(() => {
    api.get("/admin/breakglass/drills").then(({ data: next }) => setData(next))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);
  const pending = Boolean(data?.open_window) || (data?.latest_automatic && !data.latest_automatic.report);
  useEffect(() => {
    if (!pending) {
      return undefined;
    }
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [pending, load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const { data: doc } = await api.post("/admin/breakglass/drills/check");
      toast[doc.status === "passed" ? "success" : "warning"](doc.status === "passed" ? "All drill checks passed" : "Drill finished: some checks need attention");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const latest = data.latest_automatic;
  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="drill-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-1.5"><ClipboardCheck size={11} /> Breakglass drill</div>
          <h3 className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>Quarterly practice run</h3>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }} data-testid="drill-next">
            Next automatic drill {formatWhen(data.next_scheduled_at)} · report emailed to every active admin
          </p>
        </div>
        {!readOnly && <button className="btn-primary text-sm" disabled={busy} onClick={runNow} data-testid="drill-run-now">{busy ? "Running…" : "Run check now"}</button>}
      </div>
      {latest ? (
        <div className="mt-4" data-testid="drill-latest">
          <span className={`pill text-[9px] mr-2 ${STATUS[latest.status][1]}`} data-testid="drill-latest-status">{STATUS[latest.status][0]}</span>
          <span className="text-xs" style={{ color: "var(--muted)" }}>
            {latest.trigger === "scheduled" ? "Scheduled" : "Manual"} run {formatWhen(latest.started_at)}
          </span>
          {latest.report && (
            <span className="text-xs ml-2" style={{ color: latest.report.sent < latest.report.total ? "#9B2C1F" : "var(--muted)" }} data-testid="drill-report-delivery">
              · report reached {latest.report.sent} of {latest.report.total} admins{latest.report.sent < latest.report.total ? " — check email delivery" : ""}
            </span>
          )}
          {!latest.report && <span className="text-xs ml-2" style={{ color: "var(--muted)" }} data-testid="drill-report-sending">· sending report…</span>}
          <CheckList checks={latest.checks} />
        </div>
      ) : <p className="text-sm mt-4" style={{ color: "var(--muted)" }} data-testid="drill-none">No drill has run yet.</p>}
      <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--border)" }}>
        <div className="eyebrow text-[9px] mb-2">Hands-on drill</div>
        <p className="text-xs mb-3 max-w-xl" style={{ color: "var(--muted)" }}>
          Opens a {data.limits.window_minutes}-minute window in which only the chosen identity can sign in from
          the emergency page, without switching emergency access on. The result is recorded and emailed.
        </p>
        <HandsOn identities={breakglass.identities} openWindow={data.open_window} limits={data.limits} readOnly={readOnly}
          onStepUp={onStepUp} onChanged={load} />
      </div>
      <div className="mt-6 pt-5 border-t" style={{ borderColor: "var(--border)" }} data-testid="drill-history">
        <div className="eyebrow text-[9px] mb-2">Drill history</div>
        {data.history.map((drill) => (
          <div key={drill.id} className="py-1.5 text-xs flex flex-wrap gap-2" style={{ color: "var(--muted)" }} data-testid={`drill-history-${drill.id}`}>
            <span className={`pill text-[9px] ${STATUS[drill.status][1]}`}>{STATUS[drill.status][0]}</span>
            <span style={{ color: "var(--ink)" }}>{drill.kind === "automatic" ? `Automatic (${drill.trigger})` : `Hands-on · ${drill.identity_name}`}</span>
            <span>{formatWhen(drill.started_at)}{drill.run_by || drill.opened_by ? ` · ${drill.run_by || drill.opened_by}` : ""}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
